import { useEffect, useState } from "react";
import { api, money, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, CreditCard, Landmark, Banknote, Send, Pencil } from "lucide-react";

const STATUS_CLS = {
  on_hold: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  released: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  completed: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  cancelled: "bg-zinc-800 text-zinc-500 border-zinc-700",
};
const METHOD_ICON = { card: CreditCard, bank_transfer: Landmark, cash: Banknote };

export default function AdminOrders() {
  const [orders, setOrders] = useState(null);
  const [busy, setBusy] = useState({});
  const [customers, setCustomers] = useState([]);
  const [editFor, setEditFor] = useState(null);

  const load = () => api.get("/orders").then(({ data }) => setOrders(data)).catch(() => setOrders([]));
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/users").then(({ data }) => setCustomers(data)).catch(() => {}); }, []);

  const act = async (id, action, msg) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await api.post(`/orders/${id}/${action}`);
      toast.success(msg);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  if (orders === null) return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-[#ff2a5f]" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <div className="font-mono-x text-xs uppercase tracking-widest text-zinc-500">Orders</div>
        <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight text-white sm:text-3xl">All orders</h1>
      </div>
      <div className="overflow-x-auto rounded-xl border border-zinc-800 bg-[#0d0d10]">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              <TableHead className="text-zinc-400">Order</TableHead>
              <TableHead className="text-zinc-400">Customer</TableHead>
              <TableHead className="text-zinc-400">Item</TableHead>
              <TableHead className="text-zinc-400">Total</TableHead>
              <TableHead className="text-zinc-400">GST</TableHead>
              <TableHead className="text-zinc-400">Method</TableHead>
              <TableHead className="text-zinc-400">Payment</TableHead>
              <TableHead className="text-zinc-400">Status</TableHead>
              <TableHead className="text-right text-zinc-400">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.length === 0 ? (
              <TableRow className="border-zinc-800"><TableCell colSpan={9} className="py-12 text-center text-zinc-500">No orders yet.</TableCell></TableRow>
            ) : (
              orders.map((o) => {
                const Icon = METHOD_ICON[o.payment_method] || CreditCard;
                const canRelease = o.status === "on_hold";
                return (
                  <TableRow key={o.id} data-testid={`admin-order-${o.id}`} className="border-zinc-800">
                    <TableCell className="font-mono-x text-xs text-zinc-300">{o.order_number}</TableCell>
                    <TableCell className="text-white">{o.customer_email}</TableCell>
                    <TableCell className="text-zinc-300">{o.items.map((i) => i.title).join(", ")}</TableCell>
                    <TableCell className="text-white">{money(o.total)}</TableCell>
                    <TableCell className="text-zinc-300">{money(o.gst || 0)}</TableCell>
                    <TableCell><span className="inline-flex items-center gap-1.5 capitalize text-zinc-300"><Icon className="h-3.5 w-3.5" /> {o.payment_method.replace("_", " ")}</span></TableCell>
                    <TableCell className="capitalize text-zinc-300">{o.payment_status}</TableCell>
                    <TableCell>
                      <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_CLS[o.status] || STATUS_CLS.on_hold}`}>
                        {o.status.replace("_", " ")}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex flex-wrap justify-end gap-2">
                        {canRelease && (
                          <>
                            <Button data-testid={`release-${o.id}`} onClick={() => act(o.id, "release", "Order released — media granted")} disabled={busy[o.id]} size="sm" className="h-8 bg-emerald-600 text-white hover:bg-emerald-500">
                              {busy[o.id] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Release</>}
                            </Button>
                            <Button data-testid={`cancel-${o.id}`} onClick={() => act(o.id, "cancel", "Order cancelled")} disabled={busy[o.id]} size="sm" variant="outline" className="h-8 border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800">
                              <XCircle className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                        {o.payment_status === "paid" && (
                          <Button data-testid={`resend-order-${o.id}`} onClick={() => act(o.id, "resend", "Download email sent")} disabled={busy[o.id]} size="sm" variant="outline" className="h-8 border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800">
                            <Send className="mr-1 h-3.5 w-3.5" /> Resend
                          </Button>
                        )}
                        <Button data-testid={`edit-email-${o.id}`} onClick={() => setEditFor(o)} size="sm" variant="outline" className="h-8 border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {editFor && <EmailEditDialog order={editFor} customers={customers} onClose={() => setEditFor(null)} onSaved={() => { setEditFor(null); load(); }} />}
    </div>
  );
}

function EmailEditDialog({ order, customers, onClose, onSaved }) {
  const [email, setEmail] = useState(order.customer_email || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/orders/${order.id}/email`, { customer_email: email });
      toast.success("Order email updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-zinc-800 bg-zinc-900 text-white sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display">Edit order email — {order.order_number}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Label className="text-zinc-300">Customer email</Label>
          <Input data-testid="order-email-input" list="customer-emails" value={email} onChange={(e) => setEmail(e.target.value)} className="border-zinc-700 bg-zinc-950 text-white" />
          <datalist id="customer-emails">
            {customers.map((c) => <option key={c.id} value={c.email}>{c.name}</option>)}
          </datalist>
          <p className="text-xs text-zinc-500">Start typing to pick an existing customer account.</p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-zinc-400">Cancel</Button>
          <Button data-testid="save-order-email" onClick={save} disabled={busy} className="bg-[#ff2a5f] text-white hover:bg-[#e01f50]">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
